import React, { useState, useEffect, useRef } from "react";
import TreeView from "../tree/TreeView";
import FolderDragDrop from "./FolderDragDrop";
import FolderLabel from "./FolderLabel";
import { useSelector } from "react-redux";
import { createSelector } from "reselect";
import { getTreeSearch } from "../../reducers/search";
import { searchTree } from "../../reducers/_folderGeneral";
import { folderMatchesSearchString } from "../../helpers/folder";
import FlowList from "../tree-flow/FlowList";
import FolderLayoutList from "../export-layout/FolderLayoutList";
import type { Folder } from "../../types/types";
import { IAppState } from "../../types/stores/index";

type Props = {
    // Passed In
    folderType: string;
    folder: Folder; // The current folder to draw
    treeViewOnUpdate: () => void; // Used for odd/even row coloring
    showAllDuringSearch?: boolean; // Used for folder search, searched folders are told to show all contents, even if they don't match
    defaultCollapsed?: boolean;
    listComponent: "FlowList" | "FolderLayoutList";
    renamingAndHasNoChildren: boolean; // ReSELECT
    folderContainsSearch: boolean; // RESELECT Do any children of this folder match the current treeSearch?
    schedulesMode?: boolean;
    /** Lazy flow tree: invoked with folder id when this folder is shown (expanded). */
    onFlowFolderExpand?: (folderId: number) => void;
};

const ThingListFolder: React.FC<Props> = (props: Props) => {
    const {
        folderType,
        folder,
        treeViewOnUpdate,
        showAllDuringSearch,
        defaultCollapsed,
        listComponent,
        renamingAndHasNoChildren,
        folderContainsSearch,
        schedulesMode,
        onFlowFolderExpand,
    } = props;
    const [folderMatchesSearch, setFolderMatchesSearch] = useState(false);

    const getIsChildRenaming = makeGetIsChildRenaming();
    const treeSearch = useSelector<IAppState, string>(state => getTreeSearch(state));
    const flowNameSearchQuery = useSelector<IAppState, string>(state => state.flowDesigner.flowNameSearchQuery || "");
    const enabledFeatures = useSelector((state: IAppState) => state.session.enabledFeatures);
    const nameSearchActive =
        listComponent === "FlowList" &&
        flowNameSearchQuery !== "" &&
        Array.isArray(enabledFeatures) &&
        enabledFeatures.includes("fsd-flow-search-optimized");
    // Treat the flow-design name-search overlay like a tree search for folder expand / filter purposes,
    // without writing into the global tree-search slice.
    const effectiveSearch = nameSearchActive ? flowNameSearchQuery : treeSearch;
    const userId = useSelector<IAppState, number | null>(state => state.session.userId);
    const isChildRenaming = useSelector<IAppState, boolean>(state =>
        //@ts-ignore
        getIsChildRenaming(state, props)
    );
    const isRenaming = useSelector<IAppState, boolean>(
        state =>
            state.vars.renamingAudienceFolderType == folder.folderType &&
            state.vars.renamingAudienceFolderId == folder.id
    );

    // When the name-search overlay is cleared, force-collapse folders that we expanded for the search
    // (lazy mode hasn't fetched their flows, so leaving them open shows misleading "empty" rows).
    const prevNameSearchActiveRef = useRef(nameSearchActive);
    const [forceCollapseAfterSearch, setForceCollapseAfterSearch] = useState(false);
    useEffect(() => {
        if (prevNameSearchActiveRef.current && !nameSearchActive) {
            setForceCollapseAfterSearch(true);
        }
        prevNameSearchActiveRef.current = nameSearchActive;
    }, [nameSearchActive]);
    useEffect(() => {
        if (forceCollapseAfterSearch) {
            // Revert on the next render so the user can manually re-expand.
            setForceCollapseAfterSearch(false);
        }
    }, [forceCollapseAfterSearch]);

    const handleFlowFolderExpand =
        listComponent === "FlowList" &&
        onFlowFolderExpand &&
        folder.id > 0 &&
        !(treeSearch != null && treeSearch !== "") &&
        !nameSearchActive
            ? () => onFlowFolderExpand(folder.id)
            : undefined;

    useEffect(() => {
        treeViewOnUpdate();
    }, [treeViewOnUpdate]);

    useEffect(() => {
        if (effectiveSearch != null && effectiveSearch != "") {
            setFolderMatchesSearch(folderMatchesSearchString(folder, effectiveSearch));
        } else {
            setFolderMatchesSearch(false);
        }
    }, [folder, effectiveSearch]);

    const startsExpandedForLazy =
        isRenaming || isChildRenaming || (treeSearch != null && treeSearch !== "") || defaultCollapsed === false;

    const didLazyInitialFetch = useRef(false);
    useEffect(() => {
        didLazyInitialFetch.current = false;
    }, [folder.id]);

    useEffect(() => {
        if (listComponent !== "FlowList" || !onFlowFolderExpand || folder.id <= 0) {
            return;
        }
        if (!Array.isArray(enabledFeatures) || !enabledFeatures.includes("fsd-lazy-loading-flowtree")) {
            return;
        }
        // During tree search we switch to bulk GetFlows; skip per-folder lazy fetches.
        if (treeSearch != null && treeSearch !== "") {
            return;
        }
        // Name-search overlay does not need flows from `flows.byId`; skip lazy folder fetches so we
        // don't disrupt previously-loaded lazy state with a burst of GetFlowsByParentFolder calls.
        if (nameSearchActive) {
            return;
        }
        if (!startsExpandedForLazy) {
            return;
        }
        if (didLazyInitialFetch.current) {
            return;
        }
        didLazyInitialFetch.current = true;
        onFlowFolderExpand(folder.id);
    }, [
        listComponent,
        onFlowFolderExpand,
        folder.id,
        enabledFeatures,
        startsExpandedForLazy,
        isRenaming,
        isChildRenaming,
        treeSearch,
        nameSearchActive,
        defaultCollapsed,
    ]);

    const components = {
        FlowList,
        FolderLayoutList,
    };

    const renderFolderLabel = (node: Folder) => <FolderLabel node={node} folderType={folderType} />;

    let showAll = showAllDuringSearch || false;

    let collapsed: boolean | undefined = undefined;
    let itemClassName = "";
    let headerMap: null | ((x: any) => JSX.Element) = x => <FolderDragDrop folder={folder}>{x}</FolderDragDrop>;

    if (isRenaming) {
        itemClassName += " renaming";
        headerMap = null; // Don't allow drag and drop
        collapsed = renamingAndHasNoChildren; // Force folder open if renaming something with children
    }

    // Top level folders belonging to me when ALL is active - visually out
    if (folder.level == 0 && userId == folder.userId) {
        itemClassName += " whole-row-mine";
    }

    // Force open if any children are renaming
    if (isChildRenaming) {
        collapsed = false;
    }

    if (effectiveSearch) {
        const folderIsTopLevelAndMine = folder.level == 0 && userId == folder.userId;

        if (!folderContainsSearch && !folderMatchesSearch && !folderIsTopLevelAndMine) {
            return null;
        } // Don't render folder if not in search results
        if (folderMatchesSearch) {
            showAll = true;
        }
        collapsed = false; // Otherwise it's in the search results and we force open
    } else if (forceCollapseAfterSearch && listComponent === "FlowList") {
        // Search just cleared - collapse so previously-force-opened folders don't show empty.
        collapsed = true;
    }

    const ListTagName = components[listComponent];

    if (schedulesMode && (!Array.isArray(enabledFeatures) || !enabledFeatures.includes("schedules-admin"))) {
        // Ensures when not an admin the only the historical folder and non-historical folder for a user shows
        if (folder.isHistorical && !(folder.userId == 0 || folder.userId == userId)) {
            return null;
        }
    }

    return (
        <TreeView
            nodeLabel={renderFolderLabel(folder)}
            key={folder.id}
            headerMap={headerMap}
            collapsed={collapsed}
            defaultCollapsed={defaultCollapsed != null ? defaultCollapsed : true}
            itemClassName={itemClassName}
            onUpdate={treeViewOnUpdate}
            onExpand={handleFlowFolderExpand}
        >
            <ListTagName
                folderType={folderType}
                key={folder.id}
                folder={folder}
                treeViewOnUpdate={treeViewOnUpdate}
                showAllDuringSearch={showAll}
                schedulesMode={schedulesMode || false}
            />
        </TreeView>
    );
};

const makeGetIsChildRenaming = () =>
    createSelector(
        (_, props: Props) => props.folder && props.folder.children,
        (state: IAppState) => state.vars.renamingAudienceFolderId,
        (children, renamingAudienceFolderId) => searchTree(children, renamingAudienceFolderId)
    );

export default ThingListFolder;
